import React from "react";
import logo from "../assets/cloudvetx-logo.png";

const TermsAndConditions = () => {
    return (
        <div className="px-6 py-24 max-w-4xl mx-auto text-left relative">
            <div className="flex justify-between items-start">
                <div className="py-4">
                    <h1 className="text-3xl font-bold mb-6">Terms &amp; Conditions</h1>
                    <p className="mb-2"><strong>Effective Date:</strong> 13 August 2022</p>
                    <p className="mb-6"><strong>Last Updated:</strong> 29 September 2026</p>
                </div>
                <img src={logo} alt="CloudVetX Logo" className="h-40 w-40 object-contain" />
            </div>

            <p className="mb-4">
                These Terms and Conditions (“Terms”) govern your use of CloudVetX (“we,” “our,” “us”), a
                cloud-based veterinary clinic management platform. By creating an account, subscribing, or using our Services,
                you agree to be bound by these Terms. If you do not agree, do not use the Services.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">1. Definitions</h2>
            <p className="mb-4">
                <strong>“Services”</strong> means the CloudVetX software-as-a-service platform, including clinic management,
                veterinary records, client/pet data management, inventory, reporting tools, and related services.{" "}
                <strong>“Users”</strong> include clinics, veterinarians, employees, or pet owners authorised to access the Services.{" "}
                <strong>“Content”</strong> means data, records, images, or information uploaded or processed through the Services.{" "}
                <strong>“Subscription”</strong> means the paid licence (monthly or annual) granting access to the Services.{" "}
                <strong>“Applicable Laws”</strong> means all laws and professional veterinary standards applicable in the United Arab Emirates and the jurisdiction where you access the Services.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">2. Eligibility &amp; Accounts</h2>
            <ul className="list-disc list-inside mb-4">
                <li>Users must be at least 18 years old.</li>
                <li>Clinics must provide accurate, complete, and up-to-date registration information.</li>
                <li>You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</li>
                <li>We may suspend or terminate accounts using false, misleading, or fraudulent information.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">3. Scope of Services</h2>
            <p className="mb-4">
                CloudVetX provides digital tools to manage veterinary practices, including clinic, user, and employee management; pet owner contact details
                (email, phone, WhatsApp, social media); pet medical history, treatments, microchip numbers, X-rays, test results, and vaccinations; inventory and branch management;
                and notifications via integrated messaging. Enabled AI features can assist with clinical notes and diagnostic suggestions.
                A veterinarian must review, correct, and accept the output and remains responsible for clinical decisions. Feature availability depends on the plan,
                clinic configuration, rollout stage, and applicable restrictions. We may update the Services subject to the agreement, applicable law, and any required notice.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">4. User Responsibilities</h2>
            <ul className="list-disc list-inside mb-4">
                <li>Use the Services in compliance with Applicable Laws and veterinary regulations.</li>
                <li>Ensure the accuracy, legality, and integrity of all Content you upload and obtain all necessary consents.</li>
                <li>Do not attempt to hack, disrupt, copy, resell, sublicense, or reverse-engineer the software, or upload malicious code.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Data &amp; Privacy</h2>
            <p className="mb-4">
                Our Privacy Policy explains how we collect, use, and safeguard data. You retain ownership of your Content.
                You grant us a limited licence to process, host, back up, and transmit your data solely to provide the Services.
                We will maintain security measures appropriate to the processing risks as described in the DPA. No service can guarantee absolute protection against every incident.
                Our processing of clinic personal data is governed by the DPA; our own account, enquiry, and subscription processing is described in the Privacy Policy.
                Clinic payment records are bookkeeping records and do not themselves charge cards or settle payments.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Subscriptions, Fees, &amp; Taxes</h2>
            <ul className="list-disc list-inside mb-4">
                <li>Subscriptions are billed monthly or annually in advance; annual plans are discounted for a 12-month commitment.</li>
                <li>All fees are exclusive of VAT, GST, or similar taxes.</li>
                <li><strong>Taxes:</strong> We apply taxes based on your billing location and applicable regulations:
                    <ul className="list-disc list-inside ml-6 mt-2">
                        <li><strong>UAE:</strong> 5% VAT unless an exemption applies.</li>
                        <li><strong>EU:</strong> B2C – charge VAT at the customer’s local rate; B2B – reverse charge with a valid EU VAT number (via VAT OSS where applicable).</li>
                        <li><strong>UK:</strong> B2C – 20% VAT; B2B – reverse charge with a valid UK VAT number.</li>
                        <li><strong>India:</strong> B2C – 18% GST under OIDAR; B2B – reverse charge may apply with a valid GSTIN.</li>
                    </ul>
                </li>
                <li>You are responsible for any taxes we are not legally obligated to collect or remit.</li>
                <li>Non-payment may result in suspension or termination of Services.</li>
                <li><strong>Data Migration Fees:</strong> Data migration is included for up to 500 records. For larger databases, a one-time USD 500 fee applies. Migration fees are billed at the time of request.</li>
            </ul>

            <p className="mb-4">
                <strong>Founders pilot:</strong> a clinic's written pilot agreement defines the trial period, included setup and migration,
                enabled features, and any charges or terms for continued use. Express written pilot terms prevail over conflicting standard
                fee and migration provisions for that pilot. A free pilot does not itself authorise a later paid subscription.
            </p>
            <p className="mb-4">
                <strong>Planned Freemius billing:</strong> when subscription checkout through Freemius is enabled, the checkout will identify
                the seller, pricing, taxes, renewal, cancellation, and refund terms that govern the purchase. We will identify any terms
                that replace the standard billing provisions below before purchase. Subscription billing is separate from clinic client invoices.
                Freemius handles information for its own payment, tax, and fraud responsibilities under its privacy notice; our Privacy Policy
                explains the subscription information CloudVetX uses to administer access.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Cancellation &amp; Refund Policy</h2>
            <ul className="list-disc list-inside mb-4">
                <li><strong>Monthly:</strong> Cancel anytime; access continues until the end of the paid billing cycle; no refunds.</li>
                <li><strong>Annual:</strong> Full refund of subscription fees if cancelled within 30 days of purchase; after 30 days, no refunds or prorated credits; access continues until the end of the subscription period.</li>
                <li><strong>Data Migration Refunds:</strong>
                    <ul className="list-disc list-inside ml-6 mt-2">
                        <li>If migration has not been completed, all fees (including the migration fee) are fully refundable within the 30-day money-back guarantee period.</li>
                        <li>If migration has been completed, the migration fee is non-refundable. Only subscription fees remain eligible for refund under the 30-day guarantee.</li>
                    </ul>
                </li>
                <li>We may terminate your account immediately for material breach of these Terms.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">8. Intellectual Property</h2>
            <p className="mb-4">
                All intellectual property in CloudVetX software, design, branding, algorithms, and related materials is owned by
                Integrated Digital Systems / CloudVetX. We grant you a non-exclusive, non-transferable, revocable licence to use the
                Services for your internal business purposes only.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">9. Availability &amp; Support</h2>
            <p className="mb-4">
                Services are provided on an “as is” and “as available” basis. We strive for high availability but do not guarantee
                uninterrupted access. We are not responsible for downtime caused by third-party providers, internet disruptions, or
                force majeure events. Support: <a className="underline" href="mailto:support@cloudvetx.com">support@cloudvetx.com</a>.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">10. Limitation of Liability</h2>
            <p className="mb-4">
                To the maximum extent permitted by law, CloudVetX shall not be liable for indirect, incidental, punitive, or consequential damages,
                including data loss, business interruption, or lost profits. Our total liability shall not exceed the fees paid by you in the
                12 months preceding the claim.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">11. Indemnification</h2>
            <p className="mb-4">
                You agree to indemnify and hold harmless CloudVetX, its affiliates, and employees from claims, damages, or expenses arising from
                your breach of these Terms or Applicable Laws, misuse of the Services, or Content you upload or manage.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">12. Governing Law &amp; Dispute Resolution</h2>
            <p className="mb-4">
                These Terms are governed by the laws of the United Arab Emirates. Parties shall first attempt to resolve disputes amicably.
                Failing that, disputes are subject to the exclusive jurisdiction of the courts of Sharjah, UAE.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">13. Force Majeure</h2>
            <p className="mb-4">
                We are not liable for delays or failures due to causes beyond our reasonable control, including natural disasters,
                government actions, internet failures, or labour disputes.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">14. Data Processing Agreement (DPA) Reference</h2>
            <p className="mb-4">
                For clients established in the EU, UK, India, or jurisdictions requiring data processing agreements, CloudVetX provides a
                Data Processing Agreement (“DPA”) that forms part of these Terms. The DPA sets out the roles and obligations of the Clinic
                (as Controller) and CloudVetX (as Processor). By subscribing, you agree the DPA is incorporated by reference and available at
                <span> </span><a className="underline" href="[Insert DPA URL]">[Insert DPA URL]</a>.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">15. Changes to Terms</h2>
            <p className="mb-4">
                We may update these Terms from time to time. Updates will be posted with a revised “Last Updated” date.
                Continued use of the Services after changes constitutes acceptance.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">16. Contact</h2>
            <p>
                <strong>CloudVetX</strong><br />
                <strong>Email:</strong> support@cloudvetx.com<br />
                <strong>Address:</strong> Shams Business Center, Sharjah Media City, Sharjah, United Arab Emirates
            </p>
        </div>
    );
};

export default TermsAndConditions;
